import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Swal from 'sweetalert2'; // <-- Import SweetAlert2
import 'sweetalert2/dist/sweetalert2.min.css'; // <-- Import the styles
import { CreditCard, DollarSign, Package, Edit, Trash2, Plus, Tag, MessageSquare } from 'lucide-react';
import './PaymentPackages.css';

const API_BASE = process.env.REACT_APP_API_URL;

const PaymentPackages = () => {
  const emptyForm = {
    packageId: '', name: '', description: '', price: '', currency: 'INR',
    features: '', isPopular: false, isActive: true, promptId: ''
  };

  const [packages, setPackages] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPkg, setEditingPkg] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [prompts, setPrompts] = useState([]);

  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/prompts`);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (!Array.isArray(data)) throw new Error('Invalid prompts response format');
        setPrompts(data);
      } catch (err) {
        console.error('❌ Prompt Fetch Error:', err.message);
        setPrompts([]);
      }
    };
    fetchPrompts();
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/api/payment-packages`)
      .then(res => {
        if (!res.ok) return res.text().then(txt => { throw new Error(txt) });
        return res.json();
      })
      .then(setPackages)
      .catch(err => {
        console.error('Fetch packages failed:', err);
        // MODIFIED: Replaced alert with Swal
        Swal.fire({
          icon: 'error',
          title: 'Load Failed',
          text: 'Unable to load payment packages. Please check the console for details.',
        });
      });
  }, []);

  const openModal = pkg => {
    if (pkg) {
      setEditingPkg(pkg);
      setForm({
        packageId: pkg.packageId || '',
        name: pkg.name,
        description: pkg.description || '',
        price: pkg.price,
        currency: pkg.currency,
        features: pkg.features.join('\n'),
        isPopular: pkg.isPopular,
        isActive: pkg.isActive,
        promptId: pkg.promptId?._id || pkg.promptId || ''
      });
    } else {
      setEditingPkg(null);
      setForm(emptyForm);
    }
    setShowAddModal(true);
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    const payload = {
      packageId: form.packageId, name: form.name, description: form.description,
      price: Number(form.price), currency: form.currency,
      features: form.features.split('\n').map(s => s.trim()).filter(Boolean),
      isPopular: form.isPopular, isActive: form.isActive, promptId: form.promptId
    };

    if (!payload.packageId || !payload.name || form.price === '' || !payload.description) {
        // MODIFIED: Replaced alert with Swal for validation
        Swal.fire({
            icon: 'warning',
            title: 'Missing Information',
            text: 'Package ID, Name, Description, and Price are required fields.'
        });
        return;
    }

    try {
      const url = editingPkg ? `${API_BASE}/api/payment-packages/${editingPkg._id}` : `${API_BASE}/api/payment-packages`;
      const method = editingPkg ? 'PUT' : 'POST';

      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      
      // ADDED: Success Swal
      Swal.fire({
          icon: 'success',
          title: 'Saved!',
          text: `The package "${data.name}" has been saved successfully.`,
          timer: 2000,
          showConfirmButton: false,
      });

      setPackages(ps => editingPkg ? ps.map(p => (p._id === data._id ? data : p)) : [data, ...ps]);
      setShowAddModal(false);
    } catch (err) {
      console.error('Save failed:', err);
      // MODIFIED: Replaced alert with Swal
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: err.message || 'Could not save the package. Please try again.',
      });
    }
  };

  const handleDelete = (id) => {
    // MODIFIED: Replaced window.confirm with Swal
    Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
        if (result.isConfirmed) {
            try {
                const res = await fetch(`${API_BASE}/api/payment-packages/${id}`, { method: 'DELETE' });
                if (!res.ok) throw new Error(await res.text());
                Swal.fire('Deleted!', 'The package has been deleted.', 'success');
                setPackages(ps => ps.filter(p => p._id !== id));
            } catch (err) {
                console.error('Delete failed:', err);
                Swal.fire('Error!', `Could not delete package: ${err.message}`, 'error');
            }
        }
    });
  };

  const handleToggle = async (pkg) => {
    try {
      const res = await fetch(`${API_BASE}/api/payment-packages/${pkg._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !pkg.isActive })
      });
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      
      // ADDED: Success toast for a less intrusive notification
      const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      Toast.fire({
        icon: 'success',
        title: `Package ${updated.isActive ? 'activated' : 'deactivated'}`,
      });

      setPackages(ps => ps.map(p => (p._id === updated._id ? updated : p)));
    } catch (err) {
      console.error('Toggle failed:', err);
      // MODIFIED: Replaced alert with Swal
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: `Could not toggle status: ${err.message}`,
      });
    }
  };

  // ... The rest of the component's JSX remains unchanged ...
  return (
    <div className="secure-payment-packages">
      <Helmet>
        <title>{showAddModal ? `${editingPkg ? 'Edit' : 'Add'} Package | GN VISA Admin` : 'Manage Payment Packages | GN VISA Admin'}</title>
        <meta name="description" content="Create, view, edit, and manage all payment packages for user services." />
      </Helmet>
      <div className="packages-header">
        <div><p>Manage your consultation packages and pricing</p></div>
        <button className="add-package-btn" onClick={() => openModal()}><Plus size={16} /> Add New Package</button>
      </div>
      <div className="packages-grid">
        {packages.map(pkg => (
          <div key={pkg._id} className={`package-card ${pkg.isPopular ? 'popular' : ''} ${!pkg.isActive ? 'inactive' : ''}`}>
            {pkg.isPopular && <div className="popular-badge">Most Popular</div>}
            <div className="package-header">
              <h3 className="package-name">{pkg.name}</h3>
              <div className="package-price"><span className="currency">{pkg.currency}</span><span className="amount">{pkg.price}</span></div>
            </div>
            <div className="package-id-display"><Tag size={14} /><span>ID: {pkg.packageId}</span></div>
            {pkg.description && (<p className="package-description">{pkg.description}</p>)}
            {pkg.promptId && pkg.promptId.name && (<div className="package-prompt-display"><MessageSquare size={14} /><span>Prompt: {pkg.promptId.name}</span></div>)}
            <div className="package-features"><ul>{pkg.features.map((f, i) => <li key={i}>{f}</li>)}</ul></div>
            <div className="package-actions">
              <button className="action-btn edit" onClick={() => openModal(pkg)}><Edit size={16} /> Edit</button>
              <button className="action-btn delete" onClick={() => handleDelete(pkg._id)}><Trash2 size={16} /> Delete</button>
              <button className={`action-btn toggle ${pkg.isActive ? 'active' : 'inactive'}`} onClick={() => handleToggle(pkg)}>{pkg.isActive ? 'Deactivate' : 'Activate'}</button>
            </div>
          </div>
        ))}
      </div>
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingPkg ? 'Edit Package' : 'Add New Package'}</h3>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
                <div className="form-field"><label>Package ID *</label><input name="packageId" value={form.packageId} onChange={handleChange} type="text" placeholder="e.g., PKG_BASIC_49" /></div>
                <div className="form-field"><label>Package Name *</label><input name="name" value={form.name} onChange={handleChange} type="text" placeholder="e.g., Basic Consultation" /></div>
                <div className="form-field"><label>Description *</label><textarea name="description" value={form.description} onChange={handleChange} rows="3" placeholder="A brief summary of what this package includes." /></div>
                <div className="form-field"><label>Price *</label><input name="price" value={form.price} onChange={handleChange} type="number" placeholder="e.g., 49 or 0 for free" /></div>
                <div className="form-field"><label>Currency</label><input name="currency" value={form.currency} onChange={handleChange} type="text" placeholder="e.g., INR" /></div>
                <div className="form-field"><label>Features (one per line)</label><textarea name="features" value={form.features} onChange={handleChange} rows="5" placeholder={"Feature 1\nFeature 2"} /></div>
                <div className="form-field">
                  <label>Select Prompt (Optional for edits)</label>
                  <select name="promptId" value={form.promptId} onChange={handleChange}><option value="">-- No Prompt --</option>{Array.isArray(prompts) && prompts.map(p => (<option key={p._id} value={p._id}>{p.name}</option>))}</select>
                </div>
                <div className="form-field"><label className="checkbox-label"><input name="isPopular" type="checkbox" checked={form.isPopular} onChange={handleChange} /> Mark as "Most Popular"</label></div>
                <div className="form-actions">
                  <button className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                  <button className="btn-primary" onClick={handleSave}>{editingPkg ? 'Update Package' : 'Create Package'}</button>
                </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentPackages;